/**
 * Audience FIRST-PARTY, COOKIELESS. Aucun cookie, aucun identifiant persistant :
 * un id de SESSION éphémère (régénéré à chaque chargement, gardé en mémoire) groupe
 * les events d'UNE visite. Envoi groupé via `navigator.sendBeacon` (repli `fetch`
 * keepalive), vidé à la mise en arrière-plan / fermeture de l'onglet.
 *
 * Garanties : `track()` n'échoue JAMAIS et ne bloque jamais le rendu (tout est
 * encapsulé en try/catch). Endpoint same-origin (`/api/track`) → autorisé par la CSP
 * `connect-src 'self'`. Si le serveur ne répond pas (dev sans backend), c'est ignoré.
 */
interface Ev {
  name: string;
  props?: Record<string, string | number | boolean>;
}

const SESSION =
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);
const ENDPOINT = "/api/track"; // same-origin, absolu (indépendant du base path)
const BATCH = 12; // envoie sans attendre au-delà de N events
const DELAY = 4000; // sinon, regroupe pendant ~4 s
const queue: Ev[] = [];
let scheduled = false;

function refHost(): string | undefined {
  try {
    if (!document.referrer) return undefined;
    const u = new URL(document.referrer);
    return u.host === location.host ? undefined : u.host; // référent EXTERNE seulement
  } catch {
    return undefined;
  }
}

function send(): void {
  scheduled = false;
  if (queue.length === 0) return;
  const body = JSON.stringify({
    s: SESSION,
    path: location.pathname + location.hash.split("?")[0], // jamais de query string
    device: window.innerWidth < 768 ? "mobile" : "desktop",
    ref: refHost(),
    events: queue.splice(0, queue.length),
  });
  // sendBeacon renvoie false (sans throw) si la file du navigateur est pleine ou la
  // taille dépassée → on bascule sur fetch keepalive pour ne pas PERDRE le lot.
  let ok = false;
  try {
    ok = Boolean(
      navigator.sendBeacon &&
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" })),
    );
  } catch {
    ok = false; // sendBeacon a throw (quota) → repli fetch ci-dessous
  }
  if (!ok) {
    try {
      void fetch(ENDPOINT, {
        method: "POST",
        body,
        keepalive: true,
        headers: { "Content-Type": "application/json" },
      }).catch(() => {});
    } catch {
      /* dernier recours indisponible → on abandonne (analytics non bloquant) */
    }
  }
}

/** Enfile un event d'audience (regroupé puis envoyé). Ne throw jamais. */
export function track(name: string, props?: Ev["props"]): void {
  try {
    queue.push(props === undefined ? { name } : { name, props });
    if (queue.length >= BATCH) {
      send();
    } else if (!scheduled) {
      scheduled = true;
      window.setTimeout(send, DELAY);
    }
  } catch {
    /* analytics ne doit jamais casser l'app */
  }
}

/** Démarre le tracking : vue de page initiale + vidage à la fermeture/arrière-plan. */
export function initTracking(): void {
  try {
    track("pageview");
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") send();
    });
    window.addEventListener("pagehide", send);
  } catch {
    /* no-op */
  }
}
